import { Icon, Tag, Text } from '@cencosud-cencommerce/paris-uikit';

import { useAppSelector } from '@/Application/Hooks/Redux';
import {
  formatPrice,
  useGetCartTotals,
  useGiftCardData
} from '@/modules/perseo-billing/hooks';
import { PaymentProcessor } from '@/modules/perseo-billing/interfaces/src/bff-web/billing/interfaces/payment-methods.interfaceV2';
import { useMediaQuery } from '@/modules/perseo-billing/ui/react/src';

import { ShowPromoText } from '../../payment-methods-header';
import type { TPaymentMethodHeader } from '../interfaces';

import { getPaymentMethodIcon, getPaymentMethodSubtitle } from './utils';

const hideCardLabel = 'Ocultar detalles de';
const showCardLabel = 'Mostrar detalles de';

const getSaveAmountLabel = (saveAmount: number) => {
  const saveAmountFormatted = formatPrice(saveAmount);
  return 'Ahorra ' + saveAmountFormatted;
};

const PaymentMethodHeader = ({
  paymentMethod,
  isActive,
  handleClick,
  alwaysExpanded = false
}: TPaymentMethodHeader) => {
  const { data } = useAppSelector((state) => state.cart);
  const { availablePayments } = useAppSelector((state) => state.billing);
  const isMobile = useMediaQuery('lg');
  const { otherMethods, cencosudCard } = useGetCartTotals();
  const {
    getProductsNotCompatibleWithGiftCard,
    cartHasGiftCardPayment,
    getGiftCardAmount
  } = useGiftCardData();

  const cencoPayWallet = availablePayments?.wallets?.find(
    (wallet) => wallet.methodId === 'CENCOPAY'
  );
  const isEasyMarketplaceEnabled = cencoPayWallet?.canPayWithMkpEasyProducts;

  const title = paymentMethod.title ?? '';
  const subtitle = getPaymentMethodSubtitle(paymentMethod, isActive, {
    ...data,
    isEasyMarketplaceEnabled
  });
  const ariaLabel = isActive
    ? `${hideCardLabel} ${title}`
    : `${showCardLabel} ${title}`;
  const hasGiftCardApplied = cartHasGiftCardPayment();
  const productsNotCompatibleWithGC = getProductsNotCompatibleWithGiftCard();
  const methodId = paymentMethod?.methodId ?? '';
  const isEnabled = paymentMethod?.enabled ?? false;
  const isGiftCardMethod = methodId === PaymentProcessor.GIFT_CARD;
  const cencosudSaveAmount = otherMethods?.amount - cencosudCard?.amount;
  const saveAmountLabel = getSaveAmountLabel(cencosudSaveAmount);
  const giftCardAmount = getGiftCardAmount();
  const giftCardAmountFormatted = formatPrice(giftCardAmount);
  const paymentIcon = getPaymentMethodIcon(methodId, !isEnabled);

  return (
    <button
      aria-label={ariaLabel}
      className="co-flex co-flex-col co-w-full"
      onClick={handleClick}
    >
      <div className="co-w-full co-flex co-gap-8px co-p-16px lg:co-px-24px lg:co-py-8px co-cursor-pointer co-justify-between">
        <div className="co-flex co-items-center lg:co-flex-auto co-gap-16px">
          {paymentIcon}
          <div className="co-flex co-flex-col co-gap-[4px]">
            <div className="co-flex co-gap-4px">
              <p
                className={`co-font-semibold  co-text-left ${
                  isMobile
                    ? 'co-text-body1 co-leading-16px'
                    : 'co-text-h4 co-leading-24px'
                } ${!isEnabled ? 'co-text-pds-neutral-400' : 'co-text-pds-neutral-700'}`}
                tabIndex={0}
              >
                {title}
              </p>
            </div>
            <Text variant="body2" className="co-text-left">
              {subtitle}
            </Text>
            {isEnabled && isMobile && (
              <ShowPromoText id={methodId} label={saveAmountLabel} />
            )}
          </div>
        </div>
        <div className="co-flex co-justify-end co-items-center co-gap-4">
          {isEnabled && cencosudSaveAmount > 0 && !isMobile && (
            <ShowPromoText id={methodId} label={saveAmountLabel} />
          )}
          {hasGiftCardApplied && productsNotCompatibleWithGC?.length > 0 && (
            <Tag>Dscto. no acumulable con Gift Card</Tag>
          )}
          {hasGiftCardApplied && isGiftCardMethod && !isActive ? (
            <div className="co-flex co-gap-4px co-items-center">
              <Icon
                name="check-circle"
                width={20}
                height={20}
                fill="success-700"
              />
              <Text
                weight={600}
                color="success-700"
                className="co-leading-16px"
              >
                {giftCardAmountFormatted}
              </Text>
            </div>
          ) : null}
          {!alwaysExpanded && (
            <div role="button" aria-label={isActive ? 'Reducir' : 'Expandir'}>
              <Icon color="black" name={isActive ? 'arrow-up' : 'arrow-down'} />
            </div>
          )}
        </div>
      </div>
    </button>
  );
};

export { PaymentMethodHeader };
